/**
 * @file usersUtils.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-08-14
 * @description module for managing users
 */

import {
  doc,
  updateDoc,
  getDoc,
  getDocs,
  collection,
  addDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebaseConfig";

/**
 * Function to fetch public user information from the Firestore database.
 */
export const fetchPublicUserInfo = async (uid) => {
  const userRef = doc(db, "publicUserInfo", uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) {
    console.log(`requested public user document ${uid} does not exist.`);
    return null;
  }
  console.log("retrieved public user data: ", userSnap.data());
  return { uid: userSnap.id, ...userSnap.data() };
};

/**
 * Fetches public user information from the Firestore database.
 * This function retrieves user data from the 'publicUserInfo' collection, which contains public information
 * about users such as their username and profile picture (used for searching users when creating chat/inviting users to existing chat)
 *
 * @returns {Promise<Array>} A promise that resolves to an array of public user objects fetched from the Firestore database
 */
export const fetchPublicUsers = async () => {
  //grab reference to entire collection
  const usersSnapshot = await getDocs(collection(db, "publicUserInfo"));

  const publicUsers = usersSnapshot.docs.map((doc) => ({
    ...doc.data(),
    uid: doc.id,
  }));

  return publicUsers;
};

/**
 * Fetches all users from the Firestore database and returns them as an array of user objects.
 * Each user object contains the user's data along with their unique identifier (uid).
 * This will be denied by security rules (use fetchPublicUsers() instead) unless the user is an admin.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of user objects fetched from the Firestore database
 * @throws {Error} If there is an error fetching users from the database
 */
export const fetchUsers = async () => {
  //grab reference to entire collection
  const usersSnapshot = await getDocs(collection(db, "users"));

  //create a new list of objects - NOTE '...' is the spread() operator, which returns an object containing a COPY of all the fields in an iteratable (doc.data() in this case)
  const users = usersSnapshot.docs.map((doc) => ({
    ...doc.data(),
    uid: doc.id,
  }));
  //the object returned by ...doc.data() is MERGED with uid:doc.id so essentially it'll be an object almost identical to a regular document PLUS a new key-value pair

  return users;
};

/**
 * Function to fetch user information from the Firestore database based on the provided user ID (uid).
 *
 * @param {string} uid - The unique identifier of the user whose information is to be fetched.
 *
 * @returns {Promise<Object>} A promise that resolves to an object containing the user's information.
 * @throws {Error} If there is an error fetching user information from the database
 */
export const fetchUserInfo = async (uid) => {
  const userRef = doc(db, "users", uid);

  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    console.log(`requested user document ${uid} does not exist.`);
    return null;
  }

  //console.log("retrieved user data: ", userSnap.data());
  return userSnap.data();
};

/**
 * Function to get the content type of a file based on its extension.
 *
 * @param {File} file - The file object for which the content type is to be determined.
 *
 * @returns {string} The content type of the file based on its extension.
 * @throws {Error} If the file type is not recognized, it defaults to 'image/png'.
 */
export const getContentType = (file) => {
  console.log(
    "file: ",
    file,
    " file type: ",
    file.type,
    " file name: ",
    file.name,
  );
  const extension = file.name.split(".").pop().toLowerCase();

  //potentially add error handling? what if file is not recognized

  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    default:
      return "image/png"; // Fallback content type
  }
};

/*****these following 3 functions are called in AccountPage.js - 'export' makes function available to external files*****/

/**
 * Function to upload a user's profile photo to Firebase Storage and update the user's profilePicture in Firestore.
 *
 * @param {string} uid - The unique identifier of the user whose profile photo is to be uploaded.
 * @param {File} file - The file object representing the user's profile photo to be uploaded
 *
 * @returns {Promise<string>} A promise that resolves to the public URL of the uploaded profile photo.
 * @throws {Error} If there is an error uploading the profile photo or updating the user's
 */
export const uploadUserProfilePhoto = async (uid, file) => {
  try {
    // Create a reference to the storage bucket location, and get the reference
    const storageRef = ref(storage, `profilePhotos/${uid}`);
    const contentType = getContentType(file); // Determine the content type
    const metadata = {
      contentType: contentType,
    };

    //upload file and metadata to storage at storageRef. snapshot stores the metadata and reference to uploaded file
    const snapshot = await uploadBytes(storageRef, file, metadata);
    const profilePicture = await getDownloadURL(snapshot.ref); //get a public URL for the file based on the snapshot reference - profilePicture can be used to display img

    return profilePicture;
  } catch (error) {
    console.error("Error uploading profile photo:", error);
    throw error;
  }
};

/**
 * set photoURL field to argument
 *
 * @param {string} uid - The unique identifier of the user whose profile photo URL is to be updated
 * @param {string} URL - The new URL to be set for the user profile photo.
 *
 * @returns {Promise<void>} A promise that resolves when the photo URL is successfully updated in Firestore.
 * @throws {Error} If there is an error updating the photo URL in Firestore
 */
export const updatePfp = async (uid, URL) => {
  console.log("uid=", uid, " URL=", URL);

  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    profilePicture: URL,
  });

  const publicRef = doc(db, "publicUserInfo", uid);
  await updateDoc(publicRef, {
    profilePicture: URL,
  });

  //NOTE: this is not optimal, as we end up re-fetching all members, need a better way to do this
  //updating members subcollection in privateChats where user is a member
  const privateChatsRef = collection(db, "users", uid, "privateChats");
  const privateChatsSnap = await getDocs(privateChatsRef);
  privateChatsSnap.forEach(async (chatDoc) => {
    console.log("chatDoc=", chatDoc.data());

    const userRef = doc(db, "privateChats", chatDoc.id, "members", uid);
    await updateDoc(userRef, {
      profilePicture: URL,
    });

    const membersRef = collection(db, "privateChats", chatDoc.id, "members");

    //NOTE: this is not optimal, as we end up re-fetching all members, need a better way to do this
    //updating members subcollection in privateChats where user is a member
    const membersSnap = await getDocs(membersRef);
    membersSnap.forEach(async (memberDoc) => {
      const MemberRef = doc(
        db,
        "users",
        memberDoc.id,
        "privateChats",
        chatDoc.id,
      );

      const privateChatSnap = await getDoc(MemberRef);
      if (privateChatSnap.exists()) {
        await updateDoc(privateChatSnap.ref, {
          membersLastUpdated: new Date(),
        });
      }
    });
  });

  //pushing changes to members of those chats

  console.log(`User ${uid} profile picture updated to ${URL}`);
};

/**
 * set displayName field to argument
 *
 * @param {string} uid - The unique identifier of the user whose display name is to be updated
 * @param {string} name - The new display name to be set for the user
 *
 * @returns {Promise<void>} A promise that resolves when the display name is successfully updated in Firestore.
 * @throws {Error} If there is an error updating the display name in Firestore
 */
export const updateDisplayName = async (uid, name) => {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    username: name,
  });

  const publicRef = doc(db, "publicUserInfo", uid);
  await updateDoc(publicRef, {
    username: name,
  });
  console.log(`User ${uid} display updated to ${name}`);

  //update privateChats members using user's privateChats subcollection
  const privateChatsRef = collection(db, "users", uid, "privateChats");
  const privateChatsSnap = await getDocs(privateChatsRef);
  privateChatsSnap.forEach(async (chatDoc) => {
    const userRef = doc(db, "privateChats", chatDoc.id, "members", uid);
    await updateDoc(userRef, {
      username: name,
    });

    const membersRef = collection(db, "privateChats", chatDoc.id, "members");

    //NOTE: this is not optimal, as we end up re-fetching all members, need a better way to do this
    //updating members subcollection in privateChats where user is a member
    const membersSnap = await getDocs(membersRef);
    membersSnap.forEach(async (memberDoc) => {
      const MemberRef = doc(
        db,
        "users",
        memberDoc.id,
        "privateChats",
        chatDoc.id,
      );

      const privateChatSnap = await getDoc(MemberRef);
      if (privateChatSnap.exists()) {
        await updateDoc(privateChatSnap.ref, {
          membersLastUpdated: new Date(),
        });
      }
    });
  });
};

/**
 * set biography to argument
 *
 * @param {string} uid - The unique identifier of the user whose display name is to be updated
 * @param {string} name - The new display name to be set for the user
 *
 * @returns {Promise<void>} A promise that resolves when the display name is successfully updated in Firestore.
 * @throws {Error} If there is an error updating the display name in Firestore
 */
export const updateBio = async (uid, bio) => {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    bio: bio,
  });

  const publicRef = doc(db, "publicUserInfo", uid);
  await updateDoc(publicRef, {
    bio: bio,
  });
  console.log(`User ${uid} bio updated to ${bio}`);

  //update privateChats members using user's privateChats subcollection
  const privateChatsRef = collection(db, "users", uid, "privateChats");
  const privateChatsSnap = await getDocs(privateChatsRef);
  privateChatsSnap.forEach(async (chatDoc) => {
    const userRef = doc(db, "privateChats", chatDoc.id, "members", uid);
    await updateDoc(userRef, {
      bio: bio,
    });

    const membersRef = collection(db, "privateChats", chatDoc.id, "members");

    //NOTE: this is not optimal, as we end up re-fetching all members, need a better way to do this
    //updating members subcollection in privateChats where user is a member
    const membersSnap = await getDocs(membersRef);
    membersSnap.forEach(async (memberDoc) => {
      const MemberRef = doc(
        db,
        "users",
        memberDoc.id,
        "privateChats",
        chatDoc.id,
      );

      const privateChatSnap = await getDoc(MemberRef);
      if (privateChatSnap.exists()) {
        await updateDoc(privateChatSnap.ref, {
          membersLastUpdated: new Date(),
        });
      }
    });
  });
};

/**
 * Function to log website usage by a user
 * This function will create a new document for the user if it doesn't exist
 * NOTE: issue where if you login and logout to quickly, we get a "document already exists" error ... haven't found any solutions as of now.
 *
 * @param {Object} userId - userId
 * @param {string} activity - The activity performed by the user on the website
 * @param {string} subcollection - The subcollection where the activity will be logged (e.g., "loginHistory", "videoHistory")
 * @param {Date} currentTime - The current time when the activity occurred (optional)
 *
 * @returns {Promise<void>} - A promise that resolves when the activity is logged
 */
export const logWebsiteUsage = async (
  userId,
  activity,
  subcollection,
  currentTime = new Date(),
) => {
  if (!userId || !subcollection || !activity) {
    console.log("logWebsiteUsage failed: invalid arguments provided");
    return;
  }

  console.log("activity=", activity);

  const collectionRef = collection(db, "userHistory", userId, subcollection); // Using user UID as the document ID

  try {
    await addDoc(collectionRef, {
      activity,
      timestamp: currentTime,
    });
  } catch (error) {
    console.error("Error logging website usage:", error);
    console.error("Debug info:", { userId, activity, subcollection });
  }
};
